import {
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, ReactNode } from "react";
import { MdOutlineSupport } from "react-icons/md";
import { useAuthContext } from "../../hooks/auth-context";
import { useMutation } from "urql";
import { SUPPORT_REQUEST } from "../../graphql/users";
import { ToastConfiguration } from "../../constants/toast";
import { handleGraphQLError } from "../../utils/error";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SupportProps {
  trigger?: ReactNode;
}

const Support = ({ trigger }: SupportProps) => {
  const { auth } = useAuthContext();
  const toast = useToast(ToastConfiguration);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = React.useState(false);
  const [formValues, setFormValues] = React.useState({
    email: "",
    message: "",
    first_name: "",
    last_name: "",
    organization_name: "",
  });
  const [errors, setErrors] = React.useState({
    email: "",
    message: "",
    first_name: "",
    last_name: "",
    organization_name: "",
  });
  const [, supportRequestMutation] = useMutation(SUPPORT_REQUEST);

  const handleOnEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormValues({
      ...formValues,
      email: value,
    });
    if (!emailRegex.test(value)) {
      setErrors({
        ...errors,
        email: "Please enter a valid email address.",
      });
    } else {
      setErrors({
        ...errors,
        email: "",
      });
    }
  };

  const handleOnFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormValues({
      ...formValues,
      first_name: value,
    });
    if (!value.trim()) {
      setErrors({
        ...errors,
        first_name: "First name is required",
      });
    } else {
      setErrors({
        ...errors,
        first_name: "",
      });
    }
  };

  const handleOnLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormValues({
      ...formValues,
      last_name: value,
    });
    if (!value.trim()) {
      setErrors({
        ...errors,
        last_name: "Last name is required",
      });
    } else {
      setErrors({
        ...errors,
        last_name: "",
      });
    }
  };

  const handleOrganizationNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    setFormValues({
      ...formValues,
      organization_name: value,
    });
    if (!value.trim()) {
      setErrors({
        ...errors,
        organization_name: "Organization name is required",
      });
    } else {
      setErrors({
        ...errors,
        organization_name: "",
      });
    }
  };

  const validate = () => {
    let valid = true;
    const newErrors = { ...errors };
    if (!formValues.email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    }
    if (!formValues.message.trim()) {
      newErrors.message = "Message is required";
      valid = false;
    }
    if (!formValues.first_name.trim()) {
      newErrors.first_name = "First name is required";
      valid = false;
    }
    if (!formValues.last_name.trim()) {
      newErrors.last_name = "Last name is required";
      valid = false;
    }
    if (!formValues.organization_name.trim()) {
      newErrors.organization_name = "Organization name is required";
      valid = false;
    }
    if (!emailRegex.test(formValues.email)) {
      newErrors.email = "Please enter a valid email address.";
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      try {
        setLoading(true);
        const { data, error } = await supportRequestMutation({
          data: {
            email: formValues.email,
            message: formValues.message,
            first_name: formValues.first_name,
            last_name: formValues.last_name,
            organization_name: formValues.organization_name,
            type: "Support Request",
          },
        });
        if (error) {
          throw error;
        }
        if (data?.support_request) {
          toast({
            title: "Support Request",
            description: data.support_request.message,
            status: "success",
          });
          onClose();
          setFormValues({
            email: "",
            message: "",
            first_name: "",
            last_name: "",
            organization_name: "",
          });
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast({
          title: "Error",
          description: handleGraphQLError(error as Error),
          status: "error",
        });
        console.error(error, "error while handleSubmit");
      }
    }
  };

  const handleOnMessageChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setFormValues({
      ...formValues,
      message: event.target.value,
    });
  };

  useEffect(() => {
    if (auth?.user) {
      setFormValues({
        ...formValues,
        email: auth?.user?.email,
        first_name: auth?.user?.first_name || "",
        last_name: auth?.user?.last_name || "",
        organization_name: auth?.user?.organization?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const defaultTrigger = (
    <IconButton
      position="fixed"
      bottom="20px"
      right="10px"
      aria-label="Support"
      icon={<MdOutlineSupport size={24} />}
      size="md"
      borderRadius="full"
      onClick={onOpen}
    />
  );

  const triggerElement = trigger ? (
    <div onClick={onOpen} style={{ cursor: "pointer" }}>
      {trigger}
    </div>
  ) : (
    defaultTrigger
  );

  return (
    <React.Fragment>
      {triggerElement}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        isCentered={true}
        closeOnEsc={false}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent borderRadius="md">
          <ModalHeader>Need Help?</ModalHeader>
          <ModalCloseButton />
          <ModalBody px={4}>
            <VStack spacing={4} align="stretch" mb={6}>
              <Flex
                direction="column"
                p={4}
                borderRadius="md"
                border="1px solid"
                bg="gray.50"
                borderColor="gray.200"
              >
                <VStack spacing={3} align="start">
                  <Flex align="center" gap={2}>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                      Step1.
                    </Text>
                  </Flex>
                  <Text fontSize="sm" color="gray.600">
                    Check out our{" "}
                    <Text
                      as="a"
                      href="https://help.contentment.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      fontWeight="medium"
                      textDecoration="underline"
                      cursor="pointer"
                      color="gray.600"
                    >
                      FAQ and Help Center
                    </Text>{" "}
                    for answers to common questions and helpful guides.
                  </Text>
                </VStack>
              </Flex>

              <Flex
                direction="column"
                p={4}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              >
                <VStack spacing={3} align="start">
                  <Flex align="center" gap={2}>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                      Step 2.
                    </Text>
                  </Flex>
                  <Text fontSize="sm" color="gray.600">
                    If you still cannot find what you're looking for, send us a
                    message below - we'll get back to you as soon as possible.
                  </Text>
                </VStack>
              </Flex>
            </VStack>
            <VStack
              as="form"
              flex="1"
              spacing={4}
              onSubmit={handleSubmit}
              width="full"
              mt={4}
            >
              <FormControl
                id="first_name"
                isRequired
                isInvalid={!!errors.first_name}
              >
                <FormLabel>First Name</FormLabel>
                <Input
                  placeholder="Enter first name"
                  value={formValues.first_name}
                  onChange={handleOnFirstNameChange}
                />
                <FormErrorMessage>{errors.first_name}</FormErrorMessage>
              </FormControl>
              <FormControl
                id="last_name"
                isRequired
                isInvalid={!!errors.last_name}
              >
                <FormLabel>Last Name</FormLabel>
                <Input
                  placeholder="Enter last name"
                  value={formValues.last_name}
                  onChange={handleOnLastNameChange}
                />
                <FormErrorMessage>{errors.last_name}</FormErrorMessage>
              </FormControl>
              <FormControl id="email" isRequired isInvalid={!!errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  disabled={auth?.user ? true : false}
                  placeholder="Enter email"
                  value={formValues.email}
                  onChange={handleOnEmailChange}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>
              <FormControl
                id="organization_name"
                isRequired
                isInvalid={!!errors.organization_name}
              >
                <FormLabel>Organization Name</FormLabel>
                <Input
                  placeholder="Enter your organization name"
                  value={formValues.organization_name}
                  onChange={handleOrganizationNameChange}
                />
                <FormErrorMessage>{errors.organization_name}</FormErrorMessage>
              </FormControl>
              <FormControl id="message" isRequired isInvalid={!!errors.message}>
                <FormLabel>Message</FormLabel>
                <Textarea
                  variant={"outline"}
                  placeholder="Enter message"
                  value={formValues.message}
                  onChange={handleOnMessageChange}
                />
                <FormErrorMessage>{errors.message}</FormErrorMessage>
              </FormControl>
              <Divider mt={4} />
              <Flex
                justify="flex-end"
                align={"center"}
                w={"full"}
                mt={1}
                mb={2}
              >
                <Button type="submit" isLoading={loading}>
                  Submit
                </Button>
              </Flex>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </React.Fragment>
  );
};

export default Support;
